import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight, FileText, ClipboardList, Map, Wrench } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedReading } from "@/components/RelatedReading";

export const Route = createFileRoute("/resources")({
  head: () => buildSeo({
    title: "Free SEO Resources — Templates, Checklists & Playbooks | Freelancer Tamal",
    description: "Download the same SEO templates, audit checklists, content briefs, and local SEO playbooks I use with paying clients — completely free, no email wall.",
    path: "/resources",
    ogEyebrow: "Resources",
    keywords: "free SEO templates, SEO audit checklist, SEO playbook, content brief template, local SEO checklist",
  }),
  component: Resources,
});

const RES = [
  { i: ClipboardList, t: "AEO Implementation Checklist (PDF)", d: "Step-by-step playbook for getting cited by ChatGPT, Perplexity, and Gemini — schema, entity signals, crawlability, and measurement.", to: "/aeo-checklist" },
  { i: ClipboardList, t: "200-Point Technical SEO Checklist", d: "The exact list I run on every paid audit — crawl, indexation, schema, Core Web Vitals, and more.", to: "/contact" },
  { i: FileText, t: "Content Brief Template", d: "The Google Doc template my writers use to ship pages that rank. Includes SERP analysis prompts.", to: "/contact" },
  { i: Map, t: "Topical Map Starter Pack", d: "A Notion template for clustering 1,000+ keywords into a publishable content roadmap.", to: "/contact" },
  { i: Wrench, t: "Local SEO Launch Kit", d: "Google Business Profile checklist, citation list for Bangladesh + global, and a review request script.", to: "/contact" },
  { i: FileText, t: "JS SEO Pre-Deploy Checklist", d: "What to verify before shipping a React/Vue/Next migration so you don't tank rankings overnight.", to: "/contact" },
  { i: ClipboardList, t: "Monthly SEO Report Template", d: "The Looker Studio dashboard I send retainer clients — clone it for your own reporting.", to: "/contact" },
];

/**
 * Long-form guides. These live under Resources (not Services) — they're
 * educational content, so the nav, breadcrumbs, and this hub all agree.
 */
const GUIDES = [
  { t: "Online Marketing for Small Businesses", d: "The five questions small business owners ask most — how to start, what to spend, which strategy, how to advertise, and whether SEO is worth it.", to: "/online-marketing-small-businesses" },
  { t: "SEO Expert in Bangladesh", d: "What a senior SEO consultant in Bangladesh actually does, what it costs, and how to vet one.", to: "/seo-expert-bangladesh" },
  { t: "Best Digital Marketing Expert in Bangladesh", d: "Six criteria buyers use to evaluate a senior digital marketer — and how to score candidates.", to: "/best-digital-marketing-expert-bangladesh" },
  { t: "What Is Digital Marketing?", d: "A bilingual (English + বাংলা) breakdown of every channel, with real Bangladeshi examples.", to: "/what-is-digital-marketing" },
  { t: "How To Improve Domain Authority", d: "The link, content, and technical work that actually moves DA — and what wastes your money.", to: "/how-to-improve-domain-authority" },
  { t: "How To Check Domain Authority", d: "Free and paid ways to check DA, plus how to read the number without fooling yourself.", to: "/how-to-check-domain-authority" },
  { t: "Moz DA vs Ahrefs DR", d: "Side-by-side comparison of the two authority scores and when each one is worth trusting.", to: "/moz-da-vs-ahrefs-dr" },
];

function Resources() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }]} />
      <Section headingLevel={1} eyebrow="Free Resources" title={<>The templates I use with <span className="text-gradient">paying clients.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          No email wall. No "drip sequence." Just the actual files — take them,
          fork them, ship better SEO this week.
        </p>
      </Section>

      <div className="container-x grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {RES.map((r, i) => {
          const Icon = r.i;
          return (
            <Reveal key={r.t} delay={(i % 3) * 0.08}>
              <Link to={r.to} className="group block rounded-3xl border border-border bg-card p-8 h-full hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition">
                <div className="flex items-start justify-between">
                  <Icon className="h-7 w-7 text-primary" />
                  <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
                </div>
                <h2 className="mt-6 font-display text-2xl leading-tight">{r.t}</h2>
                <p className="mt-3 text-sm text-muted-foreground">{r.d}</p>
              </Link>
            </Reveal>
          );
        })}
      </div>

      <Section eyebrow="Guides" title={<>Long-form <span className="text-gradient">guides.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Deep explainers on the questions clients ask me most — free to read,
          no signup.
        </p>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {GUIDES.map((g, i) => (
            <Reveal key={g.to} delay={(i % 2) * 0.08}>
              <Link
                to={g.to}
                className="group block rounded-3xl border border-border bg-card p-8 h-full hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-display text-2xl leading-tight">{g.t}</h2>
                  <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition flex-none mt-1" />
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{g.d}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <RelatedReading
        eyebrow="Pricing & ROI"
        title={<>Pricing benchmarks and <span className="text-gradient">ROI math.</span></>}
        description="Independent cost benchmarks for SEO in Bangladesh — package scope, realistic timelines, and how to judge a quote."
        hrefs={[
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
          "/blog/how-long-does-seo-take-bangladesh-2026",
          "/blog/90-day-seo-roadmap-bangladesh-small-business-2026",
          "/blog/local-seo-cost-bangladesh-2026",
        ]}
      />

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Free SEO Resources — Templates, Checklists & Playbooks",
        url: `${SITE.url}/resources`,
        description: "Free SEO templates, audit checklists, content briefs, and local SEO playbooks.",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: [...RES, ...GUIDES].map((r, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: r.t,
            description: r.d,
            url: `${SITE.url}${r.to}`,
          })),
        },
      }} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }])} />
    </>
  );
}
